import { useState, useEffect, useMemo } from 'react';
import { useScenarioStore } from '../store/useScenarioStore';
import VehicleEditor from './VehicleEditor';

function Chevron({ open, size = 16 }: { open: boolean; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
        transition: 'transform 0.15s ease',
      }}
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}
import type { VehicleInput, FleetTCOResponse, FleetTotals } from '../types';
import { computeFleetTco } from '../utils/api';
import { useAppModeStore } from '../store/useAppModeStore';
import { useScenarioAutosave } from '../hooks/useScenarioAutosave';
import YearByCostChart, { deriveYearsFromFleetResponse } from './compare/YearByCostChart';
import DepreciationChart from './compare/DepreciationChart';
import InputCompletenessIndicator from './InputCompleteness';
import FleetHealthCheck from './FleetHealthCheck';
import ExportCsvButton from './ExportCsvButton';
import ExportPdfButton from './ExportPdfButton';
import { FlatCostModelNote } from '../pages/ScenarioComparePage';
import type { VehicleResult } from '../types';

type DistributionMethod = 'by_acquisition_cost' | 'even_split';

function fmtMoney(v: number): string {
  if (!isFinite(v)) return '—';
  return v.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

export default function FleetEditor() {
  const {
    currentScenario,
    addLineItem,
    updateLineItem,
    updateLineItemVehicle,
    removeLineItem,
    duplicateLineItem,
    setFleetName,
    patchScenario,
  } = useScenarioStore();

  const lineItems = currentScenario.line_items ?? [];
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [computeResults, setComputeResults] = useState<FleetTCOResponse | null>(null);
  const [computing, setComputing] = useState(false);
  const [computeError, setComputeError] = useState<string | null>(null);
  const [computeWarnings, setComputeWarnings] = useState<string[]>([]);
  // Phase 25.6 — scenario-level Tax Settings panel. Collapsed by default; the
  // existing per-vehicle Section 179 election toggle in VehicleEditor still
  // controls whether §179 applies to that specific vehicle. The fields here
  // set the rate / use-pct / year / cap defaults that apply to every vehicle
  // in the fleet.
  const [taxPanelOpen, setTaxPanelOpen] = useState(false);
  const marginalTaxRatePct =
    Math.round((currentScenario.marginal_tax_rate ?? 0.21) * 1000) / 10;
  const businessUsePct = Math.round((currentScenario.business_use_pct ?? 1.0) * 100);
  const taxYear = currentScenario.tax_year ?? 2026;
  const sec179Limit = currentScenario.sec179_limit ?? 1_160_000;

  // Phase 26.5 — scenario-level cost-model controls (maintenance aging,
  // fuel inflation, insurance step-up). Collapsed by default. Empty rate
  // inputs send `null` so the backend falls back to the research default;
  // entering 0 explicitly disables the escalator.
  const [costModelOpen, setCostModelOpen] = useState(false);
  const applyMaintenanceAging = currentScenario.apply_maintenance_aging ?? true;
  const fuelInflationPct = currentScenario.fuel_inflation_rate_pct ?? null;
  const insuranceStepupPct = currentScenario.insurance_stepup_rate_pct ?? null;
  // Fleet-mode defaults: gasoline+commercial. (Research file ships diesel
  // 2.5, gasoline 2.3, electricity 2.0 for fuel; commercial 6.0 for insurance.)
  // We surface a single placeholder per field so users know what they're
  // overriding even when the scenario contains a mix.
  const fuelInflationPlaceholder = '2.3 (gasoline default)';
  const insurancePlaceholder = '6.0 (commercial default)';

  // Phase 22.2: derived fleet insurance total + bulk-edit affordance.
  const calculatedFleetInsurance = useMemo(
    () =>
      lineItems.reduce((acc, li) => {
        const qty = Math.max(0, Number(li.quantity) || 0);
        const premium = Number(li.vehicle.operating_costs.annual_insurance_premium) || 0;
        return acc + qty * premium;
      }, 0),
    [lineItems],
  );
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [confirmDistribution, setConfirmDistribution] = useState<{
    total: number;
    method: DistributionMethod;
    perVehicle: number[];
  } | null>(null);

  // Phase 22.1: scenario must have a non-empty name AND ≥1 line item before
  // autosave issues its first POST. This avoids empty placeholders in the DB.
  const trimmedName = (currentScenario.name ?? '').trim();
  const readyToSave = trimmedName.length > 0 && lineItems.length > 0;
  const autosave = useScenarioAutosave({
    enabled: true,
    readyToSave,
  });

  // Reset compute results when line items change so the user re-runs.
  useEffect(() => {
    setComputeResults(null);
  }, [lineItems.length]);

  // Phase 22.1 A7: vocation validation — collected pre-compute and pre-save.
  const vocationMissing = useMemo(
    () =>
      lineItems
        .map((li, idx) => ({ idx, name: li.line_item_name || li.vehicle.label, vocation: li.vehicle.vocation_profile }))
        .filter((row) => !row.vocation),
    [lineItems],
  );

  const isFleetTierActive = useAppModeStore((s) => s.isFleetTierActive);
  const setFleetGateOpen = useAppModeStore((s) => s.setFleetGateOpen);

  async function handleComputeFleet() {
    if (lineItems.length === 0) return;
    // Phase 26 Part 2: free-tier users hitting Compute Fleet TCO open the
    // gate modal instead of firing the request. The backend also 403s but
    // we short-circuit here to avoid a noisy network roundtrip.
    if (!isFleetTierActive) {
      setFleetGateOpen(true);
      return;
    }
    if (vocationMissing.length > 0) {
      setComputeWarnings(
        vocationMissing.map((r) => `Select a vocation profile for ${r.name}.`),
      );
      // Auto-open the first offending line item for editing.
      setEditingIdx(vocationMissing[0].idx);
      return;
    }
    setComputeWarnings([]);
    setComputing(true);
    setComputeError(null);
    try {
      const res = await computeFleetTco({
        line_items: lineItems,
        scenario_annual_miles: currentScenario.annual_miles ?? 12000,
        investment_return_rate: currentScenario.investment_return_rate ?? 0.07,
        // Phase 22.7: scenario-level fuel-price defaults flow with the request
        // so the backend resolver can apply them when no per-vehicle override
        // is set.
        scenario_fuel_cost_per_gallon: currentScenario.scenario_fuel_cost_per_gallon ?? null,
        scenario_diesel_price_per_gallon: currentScenario.scenario_diesel_price_per_gallon ?? null,
        scenario_electricity_rate_per_kwh: currentScenario.scenario_electricity_rate_per_kwh ?? null,
      });
      setComputeResults(res);
    } catch (e) {
      setComputeError((e as Error).message);
    } finally {
      setComputing(false);
    }
  }

  const editingLineItem = editingIdx != null ? lineItems[editingIdx] : null;
  const horizonMonths = currentScenario.horizon_months ?? 60;

  // Phase 22.7: scenario-level fuel-price block — same pattern as
  // ScenarioEditorPage. Show only the fuel types present in the fleet's
  // line items (or all three on an empty fleet).
  const fleetFuelTypes = new Set(
    lineItems.map((li) => li.vehicle.operating_costs.fuel_type),
  );
  const showsGasolinePrice =
    lineItems.length === 0 ||
    fleetFuelTypes.has('gasoline') ||
    fleetFuelTypes.has('phev');
  const showsDieselPrice =
    lineItems.length === 0 || fleetFuelTypes.has('diesel');
  const showsElectricityPrice =
    lineItems.length === 0 ||
    fleetFuelTypes.has('electric') ||
    fleetFuelTypes.has('phev');
  const showsAnyFuelPrice =
    showsGasolinePrice || showsDieselPrice || showsElectricityPrice;
  const hasAnyScenarioFuelPrice =
    currentScenario.scenario_fuel_cost_per_gallon != null ||
    currentScenario.scenario_diesel_price_per_gallon != null ||
    currentScenario.scenario_electricity_rate_per_kwh != null;
  const resetScenarioFuelPrices = () =>
    patchScenario({
      scenario_fuel_cost_per_gallon: null,
      scenario_diesel_price_per_gallon: null,
      scenario_electricity_rate_per_kwh: null,
    });

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1
            className="text-3xl sm:text-4xl font-bold text-slate-100"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Fleet TCO Analysis
          </h1>
          <p className="text-slate-400 mt-1 text-sm sm:text-base">
            Heterogeneous fleets — mix vehicle types, fuel types, and quantities.
          </p>
        </div>
        <AutosaveIndicator state={autosave} readyToSave={readyToSave} />
      </div>

      {/* Scenario header — A1: scenario name + fleet name */}
      <div className="cnc-panel rounded-xl p-6 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Scenario Name <span className="text-red-400 normal-case font-normal">(required)</span>
            </label>
            <input
              type="text"
              className={
                'bg-slate-800 border text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none w-full ' +
                (trimmedName.length === 0
                  ? 'border-red-500 focus:border-red-400 focus:ring-1 focus:ring-red-400'
                  : 'border-slate-700 focus:border-sky-500 focus:ring-1 focus:ring-sky-500')
              }
              value={currentScenario.name ?? ''}
              onChange={(e) => patchScenario({ name: e.target.value })}
              placeholder="e.g. Q3 fleet evaluation"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Fleet Name
            </label>
            <input
              type="text"
              className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full"
              value={currentScenario.fleet_name ?? ''}
              onChange={(e) => setFleetName(e.target.value || null)}
              placeholder="e.g. Northeast delivery fleet"
            />
          </div>
        </div>
      </div>

      {/* Phase 22.7: Scenario-level Fuel Prices */}
      {showsAnyFuelPrice && (
        <div className="cnc-panel rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-3 gap-3">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Fuel Prices
              <span className="ml-2 text-slate-400 normal-case font-normal">
                (apply to every line item of that fuel type)
              </span>
            </h2>
            {hasAnyScenarioFuelPrice && (
              <button
                type="button"
                onClick={resetScenarioFuelPrices}
                className="text-xs underline underline-offset-2 text-slate-400 hover:text-slate-200"
              >
                Reset to live prices
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {showsGasolinePrice && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Gasoline ($/gal)
                </label>
                <input
                  type="number"
                  step={0.01}
                  min={0}
                  placeholder="EIA default (~$3.45)"
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full"
                  value={
                    currentScenario.scenario_fuel_cost_per_gallon == null
                      ? ''
                      : currentScenario.scenario_fuel_cost_per_gallon
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      patchScenario({ scenario_fuel_cost_per_gallon: null });
                    } else {
                      const parsed = parseFloat(raw);
                      patchScenario({
                        scenario_fuel_cost_per_gallon: isNaN(parsed)
                          ? null
                          : Math.max(0, parsed),
                      });
                    }
                  }}
                />
                <p className="text-xs text-slate-500 mt-0.5">
                  Applies to all gasoline (and PHEV) vehicles. Override per-vehicle in the vehicle editor.
                </p>
              </div>
            )}

            {showsDieselPrice && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Diesel ($/gal)
                </label>
                <input
                  type="number"
                  step={0.01}
                  min={0}
                  placeholder="EIA default (~$4.00)"
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full"
                  value={
                    currentScenario.scenario_diesel_price_per_gallon == null
                      ? ''
                      : currentScenario.scenario_diesel_price_per_gallon
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      patchScenario({ scenario_diesel_price_per_gallon: null });
                    } else {
                      const parsed = parseFloat(raw);
                      patchScenario({
                        scenario_diesel_price_per_gallon: isNaN(parsed)
                          ? null
                          : Math.max(0, parsed),
                      });
                    }
                  }}
                />
                <p className="text-xs text-slate-500 mt-0.5">
                  Applies to all diesel vehicles. Override per-vehicle in the vehicle editor.
                </p>
              </div>
            )}

            {showsElectricityPrice && (
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                  Electricity ($/kWh)
                </label>
                <input
                  type="number"
                  step={0.01}
                  min={0}
                  placeholder="EIA default (~$0.16)"
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full"
                  value={
                    currentScenario.scenario_electricity_rate_per_kwh == null
                      ? ''
                      : currentScenario.scenario_electricity_rate_per_kwh
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      patchScenario({ scenario_electricity_rate_per_kwh: null });
                    } else {
                      const parsed = parseFloat(raw);
                      patchScenario({
                        scenario_electricity_rate_per_kwh: isNaN(parsed)
                          ? null
                          : Math.max(0, parsed),
                      });
                    }
                  }}
                />
                <p className="text-xs text-slate-500 mt-0.5">
                  Applies to all EV/PHEV vehicles. Override per-vehicle in the vehicle editor.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Phase 25.6 — Scenario-level Tax Settings panel. Applies to every
          vehicle in the fleet; per-vehicle Section 179 election lives in
          VehicleEditor. Carries data-tour="tax-settings" so the Fleet
          onboarding tour Step 7 can attach here. */}
      <div data-tour="tax-settings" className="cnc-panel rounded-xl p-6 mb-6">
        <button
          type="button"
          onClick={() => setTaxPanelOpen((open) => !open)}
          className="w-full flex items-center justify-between gap-3"
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          aria-expanded={taxPanelOpen}
          aria-controls="fleet-tax-settings-body"
        >
          <span className="flex items-center gap-2 text-slate-400">
            <Chevron open={taxPanelOpen} />
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Tax Settings
              <span className="ml-2 text-slate-400 normal-case font-normal">
                (applies to all vehicles)
              </span>
            </h2>
          </span>
          <span className="text-xs text-slate-500">
            {marginalTaxRatePct}% · {businessUsePct}% business · {taxYear}
          </span>
        </button>

        {taxPanelOpen && (
          <div
            id="fleet-tax-settings-body"
            data-testid="fleet-tax-settings-body"
            className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Marginal Tax Rate
              </label>
              <div className="relative">
                <input
                  type="number"
                  step={0.5}
                  min={0}
                  max={99}
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full pr-10"
                  value={marginalTaxRatePct}
                  onChange={(e) => {
                    const pct = Math.min(99, Math.max(0, Number(e.target.value)));
                    patchScenario({ marginal_tax_rate: pct / 100 });
                  }}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
                  %
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Federal income tax rate. Common: 21% (C-corp), 22–37% (individual).
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Business Use
              </label>
              <div className="relative">
                <input
                  type="number"
                  step={1}
                  min={0}
                  max={100}
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full pr-10"
                  value={businessUsePct}
                  onChange={(e) => {
                    const pct = Math.min(100, Math.max(0, Number(e.target.value)));
                    patchScenario({ business_use_pct: pct / 100 });
                  }}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
                  %
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Vehicles used less than 100% for business have proportionally reduced deductions.
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Tax Year
              </label>
              <select
                className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 w-full"
                value={taxYear}
                onChange={(e) => patchScenario({ tax_year: Number(e.target.value) })}
              >
                <option value={2024}>2024 (60% bonus)</option>
                <option value={2025}>2025 (40% bonus)</option>
                <option value={2026}>2026 (20% bonus)</option>
                <option value={2027}>2027 (0% bonus)</option>
              </select>
              <p className="text-xs text-slate-500 mt-0.5">
                Determines bonus depreciation percentage (60%/40%/20%/0%).
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Section 179 Limit
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
                  $
                </span>
                <input
                  type="number"
                  step={10000}
                  min={0}
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg pl-7 pr-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full"
                  value={sec179Limit}
                  onChange={(e) => {
                    const v = Math.max(0, Number(e.target.value));
                    patchScenario({ sec179_limit: v });
                  }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Annual IRS limit. Default is the 2024 indexed limit ($1,160,000).
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Phase 26.5 — Cost Model (Advanced). Toggles year-over-year aging
          and inflation for maintenance, fuel, and insurance. Empty inputs
          use research-derived defaults; explicit 0 disables an escalator. */}
      <div data-tour="cost-model-advanced" className="cnc-panel rounded-xl p-6 mb-6">
        <button
          type="button"
          onClick={() => setCostModelOpen((open) => !open)}
          className="w-full flex items-center justify-between gap-3"
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          aria-expanded={costModelOpen}
          aria-controls="fleet-cost-model-body"
        >
          <span className="flex items-center gap-2 text-slate-400">
            <Chevron open={costModelOpen} />
            <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Cost Model
              <span className="ml-2 text-slate-400 normal-case font-normal">
                (Advanced — maintenance aging, fuel & insurance escalation)
              </span>
            </h2>
          </span>
          <span className="text-xs text-slate-500">
            {applyMaintenanceAging ? 'Aging on' : 'Aging off'} ·{' '}
            Fuel {fuelInflationPct == null ? 'default' : `${fuelInflationPct}%/yr`} ·{' '}
            Ins {insuranceStepupPct == null ? 'default' : `${insuranceStepupPct}%/yr`}
          </span>
        </button>

        {costModelOpen && (
          <div
            id="fleet-cost-model-body"
            data-testid="fleet-cost-model-body"
            className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4"
          >
            <label className="flex items-start gap-2 col-span-full">
              <input
                type="checkbox"
                checked={applyMaintenanceAging}
                onChange={(e) =>
                  patchScenario({ apply_maintenance_aging: e.target.checked })
                }
                className="mt-1"
              />
              <span className="text-sm text-slate-300">
                Apply maintenance aging curve
                <p className="text-xs text-slate-500">
                  Year 1 = base; year 3-5 scale up by class (NPTC / AAA / Automotive Fleet benchmarks).
                  Uncheck to keep maintenance flat across all years.
                </p>
              </span>
            </label>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Fuel Inflation
              </label>
              <div className="relative">
                <input
                  type="number"
                  step={0.1}
                  min={-10}
                  max={20}
                  placeholder={fuelInflationPlaceholder}
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full pr-10"
                  value={fuelInflationPct ?? ''}
                  onChange={(e) => {
                    const txt = e.target.value;
                    patchScenario({
                      fuel_inflation_rate_pct: txt === '' ? null : Number(txt),
                    });
                  }}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
                  %/yr
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Annual fuel-price escalator. Blank = EIA-derived default per fuel type. 0 = disabled.
              </p>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                Insurance Step-Up
              </label>
              <div className="relative">
                <input
                  type="number"
                  step={0.1}
                  min={-10}
                  max={20}
                  placeholder={insurancePlaceholder}
                  className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full pr-10"
                  value={insuranceStepupPct ?? ''}
                  onChange={(e) => {
                    const txt = e.target.value;
                    patchScenario({
                      insurance_stepup_rate_pct: txt === '' ? null : Number(txt),
                    });
                  }}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
                  %/yr
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Annual premium step-up. Blank = industry default (CIAB / NAIC). 0 = disabled.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Line items */}
      <div data-tour="fleet-line-items" className="cnc-panel rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            Fleet Line Items
            <span className="ml-2 text-slate-400 normal-case font-normal">
              {lineItems.length} {lineItems.length === 1 ? 'item' : 'items'}
            </span>
          </h2>
          <button
            data-tour="add-line-item"
            onClick={() => {
              addLineItem();
              setEditingIdx(lineItems.length);
            }}
            className="btn-secondary inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg"
          >
            + Add Line Item
          </button>
        </div>

        {lineItems.length === 0 && (
          <div
            className="rounded-xl px-5 py-8 text-sm text-center"
            style={{
              background: 'var(--panel2)',
              border: '1px dashed var(--border2)',
              color: 'var(--white-dim)',
            }}
          >
            No line items yet. Click <strong>Add Line Item</strong> to add a vehicle type and quantity.
          </div>
        )}

        <div className="space-y-3">
          {lineItems.map((li, idx) => {
            const missingVoc = !li.vehicle.vocation_profile;
            return (
              <div
                key={`${idx}-${li.vehicle.vehicle_id}`}
                className="rounded-xl px-4 py-3 flex items-center gap-3"
                style={{
                  background: 'var(--panel2)',
                  border: missingVoc ? '1px solid #c33' : '1px solid var(--border)',
                }}
              >
                <span className="text-xs font-mono text-slate-500 w-5 text-center">{idx + 1}</span>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <input
                      type="text"
                      placeholder={li.vehicle.label || 'Line item name'}
                      className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-sky-500 flex-1 min-w-[200px]"
                      value={li.line_item_name ?? ''}
                      onChange={(e) => updateLineItem(idx, { line_item_name: e.target.value || null })}
                    />
                  </div>
                  <p className="text-slate-500 text-xs mt-1">
                    {li.vehicle.label} · ${li.vehicle.depreciation.purchase_price.toLocaleString()} ·{' '}
                    {li.vehicle.operating_costs.fuel_type} ·{' '}
                    <span className={missingVoc ? 'text-red-400 font-medium' : ''}>
                      {li.vehicle.vocation_profile ?? 'no vocation — required'}
                    </span>
                  </p>
                </div>

                <div className="flex flex-col items-end gap-1 shrink-0">
                  <label className="text-[10px] uppercase tracking-widest text-slate-500 leading-none">
                    Qty
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg pl-3 pr-2 py-1.5 w-24 text-right tabular-nums focus:outline-none focus:border-sky-500"
                    value={li.quantity}
                    onChange={(e) =>
                      updateLineItem(idx, {
                        quantity: Math.max(0, Math.floor(Number(e.target.value) || 0)),
                      })
                    }
                  />
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => setEditingIdx(idx)}
                    className="btn-secondary px-3 py-1.5 text-xs rounded-lg whitespace-nowrap"
                  >
                    Edit Vehicle
                  </button>
                  <button
                    onClick={() => duplicateLineItem(idx)}
                    title="Duplicate"
                    aria-label="Duplicate line item"
                    className="w-8 h-8 inline-flex items-center justify-center text-slate-400 hover:text-sky-400 hover:bg-sky-900/20 text-sm rounded-lg"
                  >
                    ⧉
                  </button>
                  <button
                    onClick={() => removeLineItem(idx)}
                    title="Remove"
                    aria-label="Remove line item"
                    className="w-8 h-8 inline-flex items-center justify-center text-slate-400 hover:text-red-400 hover:bg-red-900/20 text-sm rounded-lg"
                  >
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Phase 22.2: Fleet insurance — derived display + bulk edit affordance */}
      <div className="cnc-panel rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Fleet Total Annual Insurance
            </p>
            <p
              data-testid="fleet-total-annual-insurance-value"
              className="text-2xl font-semibold mt-1"
              style={{ color: 'var(--tco-ink)' }}
            >
              {fmtMoney(calculatedFleetInsurance)}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Sum of per-vehicle annual premiums × quantity. Edit each line item's
              insurance in its <strong>Operating</strong> tab, or use the bulk
              edit below to set a fleet-wide total.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setBulkEditOpen(true)}
            disabled={lineItems.length === 0}
            className="btn-secondary inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg disabled:opacity-40"
          >
            ✎ Edit fleet total
          </button>
        </div>
      </div>

      {/* Phase 25: pre-compute sanity checks (input completeness + fleet
          health) sit above the Compute button so the user can review their
          inputs before running. */}
      <div data-tour="fleet-health-check">
        <FleetHealthCheck lineItems={lineItems} />
      </div>
      <InputCompletenessIndicator scenario={currentScenario} />

      <div
        className="flex items-center justify-end pt-4"
        style={{ borderTop: '1px solid var(--border)' }}
      >
        <button
          data-tour="fleet-compute"
          onClick={handleComputeFleet}
          disabled={computing || lineItems.length === 0}
          className="btn-primary px-5 py-2 text-sm rounded-lg"
        >
          {computing ? 'Computing…' : 'Compute Fleet TCO'}
        </button>
      </div>

      {(computeError || computeWarnings.length > 0) && (
        <div className="mt-4 rounded-lg bg-red-900/30 border border-red-700 px-4 py-3 text-red-300 text-sm">
          {computeWarnings.length > 0 ? (
            <ul className="list-disc list-inside space-y-1">
              {computeWarnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          ) : (
            computeError
          )}
        </div>
      )}

      {computeResults && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
            <ExportCsvButton
              kind="scenario"
              scenario={currentScenario}
              mode="fleet"
              fleetResult={computeResults}
            />
            <ExportPdfButton
              kind="scenario"
              scenario={currentScenario}
              mode="fleet"
              fleetResult={computeResults}
            />
          </div>
          <FleetResultsPanel results={computeResults} />
        </>
      )}

      {bulkEditOpen && (
        <BulkInsuranceEditor
          lineItems={lineItems}
          onCancel={() => setBulkEditOpen(false)}
          onPropose={(total, method, perVehicle) => {
            setBulkEditOpen(false);
            setConfirmDistribution({ total, method, perVehicle });
          }}
        />
      )}

      {confirmDistribution && (
        <BulkInsuranceConfirm
          lineItems={lineItems}
          proposed={confirmDistribution}
          onCancel={() => setConfirmDistribution(null)}
          onConfirm={() => {
            confirmDistribution.perVehicle.forEach((premium, idx) => {
              const li = lineItems[idx];
              if (!li) return;
              updateLineItemVehicle(idx, {
                operating_costs: {
                  ...li.vehicle.operating_costs,
                  annual_insurance_premium: Math.round(premium * 100) / 100,
                },
              });
            });
            setConfirmDistribution(null);
          }}
        />
      )}

      {editingLineItem && editingIdx != null && (
        <VehicleEditor
          vehicle={editingLineItem.vehicle}
          onSave={(updated: VehicleInput) => updateLineItemVehicle(editingIdx, updated)}
          onClose={() => setEditingIdx(null)}
          horizonMonths={horizonMonths}
          onEpaAutoFill={() => {
            // Geographic defaults wiring stays scenario-level; line items inherit.
          }}
          geoGasPriceLabel={null}
        />
      )}
    </div>
  );
}

function AutosaveIndicator({
  state,
  readyToSave,
}: {
  state: ReturnType<typeof useScenarioAutosave>;
  readyToSave: boolean;
}) {
  if (!readyToSave) {
    return (
      <p className="text-xs text-slate-500 opacity-70 mt-2 italic">
        Add a name + line item to start autosaving.
      </p>
    );
  }
  if (state.status === 'saving') {
    return <p className="text-xs text-slate-400 opacity-80 mt-2">Saving…</p>;
  }
  if (state.status === 'saved') {
    return <p className="text-xs text-emerald-400 opacity-80 mt-2">✓ Saved</p>;
  }
  if (state.status === 'error') {
    return (
      <div className="text-xs text-red-300 mt-2 flex items-center gap-2">
        <span>Autosave failed: {state.lastError}</span>
        <button
          onClick={state.retry}
          className="px-2 py-0.5 text-[11px] rounded border border-red-500 hover:bg-red-900/40"
        >
          Retry
        </button>
      </div>
    );
  }
  return <p className="text-xs text-slate-500 opacity-70 mt-2">Autosave on</p>;
}

// Phase 24.2: single-scenario fleet output. Line-item parity with the cost
// ledger in ScenarioComparePage so a fleet user viewing one scenario sees
// the same depth of detail as the side-by-side compare. Cost-line kinds
// mirror the compare-page convention:
//   one_time        : single dollar amount paid at t=0; no subtext.
//   one_time_credit : end-of-life credit; subtext = per vehicle.
//   annual          : per-year value; subtext = total over ownership period.
//   lifetime_total  : sum across ownership; subtext = annualized.
type FleetLineKind = 'one_time' | 'one_time_credit' | 'annual' | 'lifetime_total';

const FLEET_LINES: {
  key: keyof FleetTotals;
  label: string;
  kind: FleetLineKind;
  totalsRow?: boolean;
}[] = [
  { key: 'total_acquisition_cost',          label: 'Total Acquisition',           kind: 'one_time' },
  { key: 'total_annual_fuel_cost',          label: 'Annual Fuel',                 kind: 'annual' },
  { key: 'total_annual_idle_fuel_cost',     label: 'Annual Idle Fuel',            kind: 'annual' },
  { key: 'total_annual_labor_cost',         label: 'Annual Labor',                kind: 'annual' },
  { key: 'total_annual_maintenance_cost',   label: 'Annual Maintenance',          kind: 'annual' },
  { key: 'total_annual_insurance_cost',     label: 'Annual Insurance',            kind: 'annual' },
  { key: 'total_annual_registration_cost',  label: 'Annual Registration',         kind: 'annual' },
  { key: 'total_annual_operating_cost',     label: 'Total Operating / Year',      kind: 'annual' },
  { key: 'total_residual_reclaimed',        label: 'Residual Value Reclaimed',    kind: 'one_time_credit' },
  { key: 'total_tax_benefit',               label: 'Tax Benefit (Sec. 179 + Bonus)', kind: 'one_time_credit' },
  { key: 'total_tco_over_period',           label: 'Total TCO (period)',          kind: 'lifetime_total', totalsRow: true },
];

function fleetLineSubtext(
  kind: FleetLineKind,
  value: number,
  ownershipYears: number,
  vehicleCount: number,
): string {
  if (!isFinite(value) || value === 0) return '';
  switch (kind) {
    case 'one_time':
      return '';
    case 'one_time_credit': {
      if (vehicleCount <= 0) return '';
      return `${fmtMoney(value / vehicleCount)} per vehicle`;
    }
    case 'annual': {
      if (ownershipYears <= 0) return '';
      const total = value * ownershipYears;
      const yearsLabel = ownershipYears === 1 ? 'year' : 'years';
      return `= ${fmtMoney(total)} over ${ownershipYears.toFixed(0)} ${yearsLabel}`;
    }
    case 'lifetime_total': {
      if (ownershipYears <= 0) return '';
      return `${fmtMoney(value / ownershipYears)}/yr`;
    }
  }
}

function FleetResultsPanel({ results }: { results: FleetTCOResponse }) {
  const t = results.fleet_totals;
  const m = results.fleet_metadata;
  const ownershipYears = t.ownership_period_years;
  const vehicleCount = t.vehicle_count;
  // Phase 25: annual-miles-per-vehicle for the KPI hero. Lifetime miles
  // divided by ownership years gives the fleet-wide annual mileage; divide
  // by vehicle count for the per-vehicle number that decision-makers think in.
  const annualMilesPerVehicle =
    vehicleCount > 0 && ownershipYears > 0
      ? Math.round(t.total_lifetime_miles / ownershipYears / vehicleCount)
      : 0;
  // Phase 24.2: tax benefit row gated like the compare table — hide when
  // the value is zero (tax-exempt entity or all line items ineligible).
  const visibleLines = FLEET_LINES.filter((line) => {
    if (line.key !== 'total_tax_benefit') return true;
    return Number(t[line.key] ?? 0) > 0;
  });

  return (
    <div data-testid="fleet-tco-summary" className="cnc-panel rounded-xl p-6 mt-6">
      <h2
        className="text-xs font-semibold uppercase tracking-widest mb-4"
        style={{ color: 'var(--tco-ink-3)' }}
      >
        Fleet TCO Summary
      </h2>

      {/* Phase 25: headline KPI row. Surfaces the three numbers fleet operators
          care about most — cost per mile, annual miles per vehicle, total TCO —
          without requiring the user to scan the ledger. Stacks on mobile. */}
      <div
        data-testid="fleet-kpi-hero"
        data-tour="cpm-hero"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 16,
          background: 'var(--tco-surface)',
          border: '1px solid var(--tco-line)',
          borderRadius: 8,
          padding: '16px 20px',
          marginBottom: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              color: 'var(--tco-ink-3)',
              fontWeight: 500,
              marginBottom: 4,
            }}
          >
            Cost per Mile
          </div>
          <div
            data-testid="kpi-cost-per-mile"
            className="tco-num"
            style={{
              fontSize: 28,
              fontWeight: 600,
              color: 'var(--tco-ink)',
              letterSpacing: '-0.015em',
              lineHeight: 1.1,
            }}
          >
            ${t.fleet_cost_per_mile.toFixed(2)}
            <span style={{ fontSize: 14, color: 'var(--tco-ink-3)', marginLeft: 4, fontWeight: 400 }}>
              / mile
            </span>
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              color: 'var(--tco-ink-3)',
              fontWeight: 500,
              marginBottom: 4,
            }}
          >
            Annual Miles
          </div>
          <div
            data-testid="kpi-annual-miles"
            className="tco-num"
            style={{
              fontSize: 28,
              fontWeight: 600,
              color: 'var(--tco-ink)',
              letterSpacing: '-0.015em',
              lineHeight: 1.1,
            }}
          >
            {annualMilesPerVehicle.toLocaleString('en-US')}
            <span style={{ fontSize: 14, color: 'var(--tco-ink-3)', marginLeft: 4, fontWeight: 400 }}>
              mi/yr
            </span>
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              color: 'var(--tco-ink-3)',
              fontWeight: 500,
              marginBottom: 4,
            }}
          >
            {ownershipYears > 0 ? `${Math.round(ownershipYears)}-Year TCO` : 'Total TCO'}
          </div>
          <div
            data-testid="kpi-total-tco"
            className="tco-num"
            style={{
              fontSize: 28,
              fontWeight: 600,
              color: 'var(--tco-ink)',
              letterSpacing: '-0.015em',
              lineHeight: 1.1,
            }}
          >
            {fmtMoney(t.total_tco_over_period)}
          </div>
        </div>
      </div>

      {/* Cost ledger — line-item parity with ScenarioComparePage */}
      <div
        style={{
          background: 'var(--tco-surface)',
          border: '1px solid var(--tco-line)',
          borderRadius: 8,
          overflow: 'hidden',
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.4fr',
            background: 'var(--tco-line-soft)',
            borderBottom: '1px solid var(--tco-line)',
            padding: '10px 16px',
            fontSize: 11,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            color: 'var(--tco-ink-3)',
            fontWeight: 500,
          }}
        >
          <div>Cost Line</div>
          <div style={{ textAlign: 'right' }}>Value</div>
        </div>
        {visibleLines.map((line, i) => {
          const value = Number(t[line.key] ?? 0);
          const sub = fleetLineSubtext(line.kind, value, ownershipYears, vehicleCount);
          const isCredit = line.kind === 'one_time_credit';
          const display = isCredit && value > 0 ? `−${fmtMoney(value)}` : fmtMoney(value);
          const isTotals = !!line.totalsRow;
          return (
            <div
              key={line.key as string}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.4fr',
                padding: '11px 16px',
                borderTop: i === 0 ? 'none' : '1px solid var(--tco-line-soft)',
                background: isTotals ? 'var(--tco-line-soft)' : 'transparent',
                fontWeight: isTotals ? 600 : 400,
                fontSize: 14,
                color: 'var(--tco-ink)',
              }}
            >
              <div>{line.label}</div>
              <div
                style={{
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                  fontFeatureSettings: "'tnum'",
                }}
              >
                <div>{display}</div>
                {sub && (
                  <div style={{ fontSize: 10, color: 'var(--tco-ink-3)', fontWeight: 400, marginTop: 2 }}>
                    {sub}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Descriptive rows — vehicle count, cost per mile, fuel mix, vocation mix */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.4fr',
            padding: '11px 16px',
            borderTop: '1px solid var(--tco-line-soft)',
            fontSize: 14,
            color: 'var(--tco-ink)',
          }}
        >
          <div>Vehicle Count</div>
          <div style={{ textAlign: 'right' }}>{vehicleCount}</div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.4fr',
            padding: '11px 16px',
            borderTop: '1px solid var(--tco-line-soft)',
            fontSize: 14,
            color: 'var(--tco-ink)',
          }}
        >
          <div>Cost / Mile</div>
          <div
            style={{
              textAlign: 'right',
              fontVariantNumeric: 'tabular-nums',
              fontFeatureSettings: "'tnum'",
            }}
          >
            ${t.fleet_cost_per_mile.toFixed(3)}
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.4fr',
            padding: '11px 16px',
            borderTop: '1px solid var(--tco-line-soft)',
            fontSize: 13,
            color: 'var(--tco-ink-2)',
          }}
        >
          <div>Fuel Type Mix</div>
          <div style={{ textAlign: 'right' }}>
            {Object.entries(m.fuel_type_distribution)
              .map(([k, v]) => `${k}: ${v}`)
              .join(' · ') || '—'}
          </div>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.4fr',
            padding: '11px 16px',
            borderTop: '1px solid var(--tco-line-soft)',
            fontSize: 13,
            color: 'var(--tco-ink-2)',
          }}
        >
          <div>Vocation Mix</div>
          <div style={{ textAlign: 'right' }}>
            {Object.entries(m.vocation_distribution)
              .map(([k, v]) => `${k}: ${v}`)
              .join(' · ') || '—'}
          </div>
        </div>
      </div>

      {/* Phase 24.3: Year-by-year stacked cost walk. Aggregated across all
          fleet line items. Derived on-frontend from the cumulative cost time
          series + per-year operating totals already in the response. */}
      <div data-tour="year-by-year-chart" style={{ marginTop: 24, marginBottom: 16 }}>
        <h3
          className="text-xs font-semibold uppercase tracking-widest mb-3"
          style={{ color: 'var(--tco-ink-3)' }}
        >
          Year-by-Year Cost Walk
        </h3>
        <YearByCostChart
          years={deriveYearsFromFleetResponse(results)}
          totalFuel={(t.total_annual_fuel_cost ?? 0) * (t.ownership_period_years ?? 0)}
          totalDepreciation={Math.max(0, (t.total_acquisition_cost ?? 0) - (t.total_residual_reclaimed ?? 0))}
          fleetVehicleCount={vehicleCount}
        />
        <FlatCostModelNote />
      </div>

      {/* Phase 24.3.1: Per-line-item residual value curves. Each fleet line
          item exposes its own monthly_value_path; rendering them together
          surfaces the per-vehicle depreciation shape that drives the
          residual-reclaimed total in the ledger above. */}
      <div style={{ marginTop: 24, marginBottom: 16 }}>
        <h3
          className="text-xs font-semibold uppercase tracking-widest mb-3"
          style={{ color: 'var(--tco-ink-3)' }}
        >
          Vehicle Value Over Time
        </h3>
        <DepreciationChart
          vehicles={results.line_items.map((li, i): VehicleResult => {
            const pv = li.per_vehicle_tco as Record<string, unknown>;
            const dep = (pv?.['depreciation'] as Record<string, unknown>) ?? {};
            return {
              vehicle_id: `${li.line_item_name}-${i}`,
              label: li.vehicle_label,
              depreciation: dep,
              financing: (pv?.['financing'] as Record<string, unknown>) ?? {},
              operating: (pv?.['operating'] as Record<string, unknown>) ?? {},
              tco: (pv?.['tco'] as Record<string, unknown>) ?? {},
              emotional: {} as Record<string, unknown>,
              capital: {} as Record<string, unknown>,
            };
          })}
          purchasePrices={results.line_items.map((li) => {
            const pv = li.per_vehicle_tco as Record<string, unknown>;
            const dep = pv?.['depreciation'] as Record<string, unknown> | undefined;
            const path = dep?.['monthly_value_path'];
            return Array.isArray(path) && typeof path[0] === 'number' ? (path[0] as number) : null;
          })}
        />
      </div>

      <div className="text-xs space-y-1" style={{ color: 'var(--tco-ink-3)' }}>
        <div>
          MPG data quality:{' '}
          {Object.entries(m.mpg_data_quality)
            .map(([k, v]) => `${k}: ${v}`)
            .join(', ') || 'none'}
        </div>
      </div>
    </div>
  );
}

// Phase 22.2: bulk insurance edit dialog. Asks the user for a fleet-total
// premium + distribution method, computes per-vehicle premiums, and hands
// them to the confirm step. Does NOT mutate state.
function BulkInsuranceEditor({
  lineItems,
  onCancel,
  onPropose,
}: {
  lineItems: import('../types').LineItem[];
  onCancel: () => void;
  onPropose: (total: number, method: DistributionMethod, perVehicle: number[]) => void;
}) {
  const [totalStr, setTotalStr] = useState('');
  const [method, setMethod] = useState<DistributionMethod>('by_acquisition_cost');

  function handleConfirm() {
    const total = Number(totalStr);
    if (!isFinite(total) || total <= 0) return;
    const perVehicle = distributePremium(lineItems, total, method);
    onPropose(total, method, perVehicle);
  }

  return (
    <ModalShell onClose={onCancel}>
      <h3 className="text-lg font-semibold text-slate-100 mb-1">Set fleet total annual insurance</h3>
      <p className="text-xs text-slate-400 mb-4">
        Distributes a fleet-wide total across each line item, then writes the
        resulting per-vehicle premium into each vehicle.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            Total Annual Premium
          </label>
          <input
            type="number"
            min={0}
            step={1}
            className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 w-full"
            value={totalStr}
            onChange={(e) => setTotalStr(e.target.value)}
            placeholder="$"
            autoFocus
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            Distribution Method
          </label>
          <select
            className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:outline-none focus:border-sky-500 w-full"
            value={method}
            onChange={(e) => setMethod(e.target.value as DistributionMethod)}
          >
            <option value="by_acquisition_cost">By acquisition cost (proportional)</option>
            <option value="even_split">Even split (per-vehicle)</option>
          </select>
        </div>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed mt-3">
        <em>By acquisition cost:</em> larger / more-expensive vehicles get a
        larger share (rough proxy for higher policy cost).{' '}
        <em>Even split:</em> total ÷ vehicle count.
      </p>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onCancel} className="btn-secondary px-4 py-2 text-sm rounded-lg">
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={!(Number(totalStr) > 0)}
          className="btn-primary px-4 py-2 text-sm rounded-lg disabled:opacity-40"
        >
          Preview distribution
        </button>
      </div>
    </ModalShell>
  );
}

function BulkInsuranceConfirm({
  lineItems,
  proposed,
  onCancel,
  onConfirm,
}: {
  lineItems: import('../types').LineItem[];
  proposed: { total: number; method: DistributionMethod; perVehicle: number[] };
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <ModalShell onClose={onCancel}>
      <h3 className="text-lg font-semibold text-slate-100 mb-1">
        Replace per-vehicle insurance values?
      </h3>
      <p className="text-xs text-slate-400 mb-4">
        This will overwrite each line item's annual insurance premium with the
        distributed share. The fleet total will then equal{' '}
        <strong>{fmtMoney(proposed.total)}</strong> ({proposed.method.replace(/_/g, ' ')}).
      </p>
      <div className="rounded-lg overflow-hidden border border-slate-700 mb-5">
        <table className="w-full text-xs">
          <thead className="bg-slate-800/60 text-slate-400">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Line item</th>
              <th className="text-right px-3 py-2 font-medium">Qty</th>
              <th className="text-right px-3 py-2 font-medium">Current</th>
              <th className="text-right px-3 py-2 font-medium">After</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.map((li, idx) => {
              const current = Number(li.vehicle.operating_costs.annual_insurance_premium) || 0;
              const after = proposed.perVehicle[idx] ?? 0;
              return (
                <tr key={idx} className="border-t border-slate-800">
                  <td className="px-3 py-2 text-slate-200">
                    {li.line_item_name || li.vehicle.label || `Line ${idx + 1}`}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-300">{li.quantity}</td>
                  <td className="px-3 py-2 text-right text-slate-400">{fmtMoney(current)}</td>
                  <td className="px-3 py-2 text-right text-slate-100 font-medium">
                    {fmtMoney(after)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="btn-secondary px-4 py-2 text-sm rounded-lg">
          Cancel
        </button>
        <button onClick={onConfirm} className="btn-primary px-4 py-2 text-sm rounded-lg">
          Apply distribution
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      onClick={onClose}
    >
      <div
        className="cnc-panel rounded-xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

// Mirrors the backend allocator (apps/api/services/fleet_calc.py before
// 22.2 refactor) so users see the same numbers locally as the server would
// have produced. Returns one premium per line item; zero-quantity items
// receive 0 and are excluded from the share denominator.
function distributePremium(
  lineItems: import('../types').LineItem[],
  total: number,
  method: DistributionMethod,
): number[] {
  const out = new Array(lineItems.length).fill(0);
  const eligibleIdx: number[] = [];
  lineItems.forEach((li, i) => {
    if ((Number(li.quantity) || 0) > 0) eligibleIdx.push(i);
  });
  if (eligibleIdx.length === 0 || total <= 0) return out;

  if (method === 'even_split') {
    const totalQty = eligibleIdx.reduce((acc, i) => acc + Number(lineItems[i].quantity), 0);
    if (totalQty <= 0) return out;
    const perVehicle = total / totalQty;
    eligibleIdx.forEach((i) => {
      out[i] = perVehicle;
    });
    return out;
  }

  // by_acquisition_cost
  const weights = eligibleIdx.map((i) => {
    const li = lineItems[i];
    const acq =
      Number(li.vehicle.depreciation.purchase_price || 0) +
      Number(li.vehicle.upfit_cost || 0) +
      Number(li.vehicle.charging_infrastructure_cost || 0);
    return Math.max(acq, 0) * Number(li.quantity);
  });
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  if (totalWeight <= 0) {
    const totalQty = eligibleIdx.reduce((acc, i) => acc + Number(lineItems[i].quantity), 0);
    const perVehicle = totalQty > 0 ? total / totalQty : 0;
    eligibleIdx.forEach((i) => {
      out[i] = perVehicle;
    });
    return out;
  }
  eligibleIdx.forEach((i, k) => {
    const share = (weights[k] / totalWeight) * total;
    const qty = Number(lineItems[i].quantity);
    out[i] = qty > 0 ? share / qty : 0;
  });
  return out;
}
